"use client";

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { runAction } from "@/lib/actions/client";
import {
  inviteSeatAction,
  listSeatsAction,
  revokeSeatAction,
} from "@/lib/billing/actions";
import type { Seat } from "@/lib/billing/types";

/**
 * Seat console for seat-metered subscriptions. Authorization is RLS:
 * members see seats, only org owner/admin can invite/revoke — errors from
 * non-admins surface as messages, no client-side role logic.
 */
export function SeatsConsole({ orgId, seatCap }: { orgId: string; seatCap: number }) {
  const [seats, setSeats] = useState<Seat[] | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const reload = useCallback(async () => {
    const r = await runAction(listSeatsAction, { org_id: orgId });
    if (r.ok) setSeats(r.value as Seat[]);
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    runAction(listSeatsAction, { org_id: orgId }).then((r) => {
      if (!cancelled && r.ok) setSeats(r.value as Seat[]);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const r = await runAction(inviteSeatAction, { org_id: orgId, email: inviteEmail });
      if (!r.ok) {
        setError(
          r.error._tag === "SeatActionFailed"
            ? "Invite failed — check the seat cap and that you are an org admin."
            : "Invite failed.",
        );
        return;
      }
      setInviteEmail("");
      await reload();
    } finally {
      setPending(false);
    }
  }

  async function revoke(seatId: string) {
    setError(null);
    const r = await runAction(revokeSeatAction, { seat_id: seatId });
    if (!r.ok) {
      setError("Revoke failed — only org admins can revoke seats.");
      return;
    }
    await reload();
  }

  const live = (seats ?? []).filter((s) => !s.revoked_at);

  return (
    <div>
      <p className="mt-2 text-sm text-[var(--muted)]">
        {seats === null
          ? "Loading seats…"
          : `${live.length}${seatCap > 0 ? ` of ${seatCap}` : ""} seats in use. Invited teammates claim their seat by signing in with the invited email.`}
      </p>

      {live.length > 0 && (
        <ul className="mt-4 divide-y divide-white/10 rounded-xl border border-white/10">
          {live.map((s) => (
            <li key={s.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-white">
                {s.invited_email}
                <span className="ml-2 text-xs text-[var(--muted)]">
                  {s.claimed_at ? "claimed" : "invited"}
                </span>
              </span>
              <button
                type="button"
                onClick={() => revoke(s.id)}
                className="text-xs text-[var(--muted)] transition hover:text-rose-300"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={invite} className="mt-4 flex gap-3">
        <input
          type="email"
          required
          placeholder="teammate@company.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          className="flex-1 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-fuchsia-400/50"
        />
        <button
          type="submit"
          disabled={pending}
          className="rounded-full border border-white/15 bg-[var(--bg)]/45 px-5 py-2 text-sm font-medium text-white backdrop-blur-md transition hover:border-white/25 disabled:opacity-50"
        >
          Invite
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-rose-300">
          {error}
        </p>
      )}
    </div>
  );
}
